import { useEffect, useState } from "react";
import { Check, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UPGRADE_EVENT, PLANS, startCheckout, errorMessage } from "@/lib/billing";
import { useUsage } from "@/lib/usage";

export const PRO_PERKS = [
  "Unlimited AI drafts every month",
  "500 Edgar AI prompts a month",
  "Full sales analytics dashboard",
  "Sell-through, category & 12-month revenue trends",
  "Cancel anytime from Plan & Billing",
];

export function GoProButton({ className = "", testid = "go-pro-btn", plan = "monthly", label, variant }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await startCheckout(PLANS[plan].lookupKey);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't start checkout"));
      setBusy(false);
    }
  };
  return (
    <Button type="button" onClick={go} disabled={busy} data-testid={testid}
      className={`h-11 rounded-full px-6 font-semibold ${variant === "outline" ? "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50" : "bg-[#0053DA] text-white hover:bg-[#0043B0]"} ${className}`}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className="mr-2 h-4 w-4" />}
      {label || PLANS[plan].label}
    </Button>
  );
}

export function PerkList() {
  return (
    <ul className="space-y-2 text-sm text-slate-700" data-testid="pro-perk-list">
      {PRO_PERKS.map((p) => (
        <li key={p} className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
        </li>
      ))}
    </ul>
  );
}

export function IntervalToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium" role="tablist" data-testid="plan-interval-toggle">
      {["monthly", "yearly"].map((k) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`plan-interval-${k}`}
          className={`rounded-full px-3 py-1.5 transition-colors ${value === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
          {k === "monthly" ? "Monthly" : "Yearly"}
          {k === "yearly" ? <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{PLANS.yearly.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function PriceTag({ plan }) {
  return (
    <p className="text-slate-900" data-testid="plan-price-tag">
      <span className="text-4xl font-bold">{PLANS[plan].price}</span>
      <span className="text-sm text-slate-500"> {PLANS[plan].per}</span>
      {plan === "yearly" ? <span className="ml-2 text-xs text-slate-500">≈ $12.50/mo, save 17%</span> : null}
    </p>
  );
}

export default function UpgradeDialog() {
  const [interval, setInterval] = useState("monthly");
  const { isStarter } = useUsage();
  const [state, setState] = useState({ open: false, message: "" });

  useEffect(() => {
    const onUpgrade = (ev) => setState({ open: true, message: ev.detail?.message || "" });
    window.addEventListener(UPGRADE_EVENT, onUpgrade);
    return () => window.removeEventListener(UPGRADE_EVENT, onUpgrade);
  }, []);

  return (
    <Dialog open={state.open} onOpenChange={(open) => setState((s) => ({ ...s, open }))}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="upgrade-dialog">
        <DialogHeader>
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <Crown className="h-5 w-5" />
          </div>
          <DialogTitle className="text-2xl font-bold tracking-tight">Upgrade to SnapList Pro</DialogTitle>
          <DialogDescription data-testid="upgrade-dialog-message">
            {state.message || "Unlock unlimited AI drafts and full sales analytics."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-start gap-3">
          <IntervalToggle value={interval} onChange={setInterval} />
          <PriceTag plan={interval} />
        </div>
        <PerkList />
        <div className="mt-2 space-y-2">
          <GoProButton className="w-full" testid="upgrade-dialog-go-pro" plan={interval}
            label={isStarter ? `Switch to Pro — ${PLANS[interval].price}${PLANS[interval].per.replace("/month", "/mo").replace("/year", "/yr")}` : undefined} />
          {!isStarter ? (
            <GoProButton className="w-full" variant="outline" testid="upgrade-dialog-starter" plan="starter"
              label="Or Starter — $13/mo for 100 drafts" />
          ) : null}
          <p className="text-center text-xs text-slate-500">
            Pay with card, Apple Pay, Google Pay, Link and more at secure Stripe checkout.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
